import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  UserPlus,
  Mail,
  Phone,
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Edit3,
  Key
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import { getAdminLevelDisplay, getAdminLevelDescription } from '@/lib/permissions'
import { useSubscription, useCanAddAdmin } from '@/hooks/useSubscription'
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt'

interface AdminProfile {
  id: string
  email: string
  first_name: string
  last_name: string
  phone: string | null
  role: 'admin' | 'counselor' | 'leader' | 'pastor' | 'member'
  admin_level: 'church_owner' | 'church_admin' | 'church_elder' | 'ministry_leader' | 'none'
  two_factor_enabled: boolean
  created_at: string
  church_id: string
  member_id?: string
}

interface Member {
  id: string
  preferred_name: string
  phone_number: string | null
  email: string | null
  role: string
  created_at: string
  church_id: string
  is_active: boolean
}

interface AdminManagementProps {
  user: AdminUser | null
}

export default function AdminManagement({ user }: AdminManagementProps) {
  const [admins, setAdmins] = useState<AdminProfile[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const { limits, usage } = useSubscription(user?.churchId)
  const { canAdd: canAddAdmin, current, max } = useCanAddAdmin(user?.churchId)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)

  // New admin form
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    role: 'admin' as 'admin' | 'counselor' | 'leader' | 'pastor',
    adminLevel: 'ministry_leader' as 'church_owner' | 'church_admin' | 'church_elder' | 'ministry_leader'
  })

  // Promote member form
  const [promoteData, setPromoteData] = useState({
    password: '',
    role: 'counselor' as 'admin' | 'counselor' | 'leader' | 'pastor',
    adminLevel: 'ministry_leader' as 'church_owner' | 'church_admin' | 'church_elder' | 'ministry_leader'
  })

  useEffect(() => {
    loadAdmins()
    loadMembers()
  }, [])

  async function loadAdmins() {
    try {
      setLoading(true)

      // Get current user's church
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) return

      // Load all admin profiles and counselors for this church
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('church_id', profile.church_id)
        .or('admin_level.in.(church_owner,church_admin,church_elder,ministry_leader),role.eq.counselor')
        .order('created_at', { ascending: false })

      if (error) throw error

      setAdmins(data || [])
    } catch (error) {
      console.error('Error loading admins:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadMembers() {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) return

      // Load members who are not yet promoted to admin/counselor roles
      // We check if they have a linked profile with admin-type role
      const { data: allMembers, error: membersError } = await supabase
        .from('members')
        .select('*')
        .eq('church_id', profile.church_id)
        .eq('is_active', true)
        .order('preferred_name')

      if (membersError) throw membersError

      // Get all profiles to see which members are already admins
      const { data: profiles } = await supabase
        .from('profiles')
        .select('member_id, role')
        .eq('church_id', profile.church_id)
        .in('role', ['admin', 'counselor', 'leader', 'pastor'])

      const promotedMemberIds = new Set((profiles || []).map(p => p.member_id).filter(Boolean))

      // Filter out members who are already promoted
      const availableMembers = (allMembers || []).filter(m => !promotedMemberIds.has(m.id))

      setMembers(availableMembers)
    } catch (error) {
      console.error('Error loading members:', error)
    }
  }

  async function handleAddAdmin(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setMessage(null)

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        setMessage({ type: 'error', text: 'Not authenticated' })
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id, admin_level')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) {
        setMessage({ type: 'error', text: 'No church ID found' })
        return
      }

      // Only church owners can add admins
      if (profile.admin_level !== 'church_owner') {
        setMessage({ type: 'error', text: 'Only church owners can add admin users' })
        return
      }

      // Create the user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: newAdmin.email,
        password: newAdmin.password,
        email_confirm: true,
        user_metadata: {
          first_name: newAdmin.firstName,
          last_name: newAdmin.lastName
        }
      })

      if (authError) throw authError

      // Create profile entry
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: newAdmin.email,
          first_name: newAdmin.firstName,
          last_name: newAdmin.lastName,
          phone: newAdmin.phone || null,
          church_id: profile.church_id,
          role: newAdmin.role,
          admin_level: newAdmin.role === 'admin' ? newAdmin.adminLevel : 'none',
          email_verified: true,
          two_factor_enabled: false,
        })

      if (profileError) throw profileError

      setMessage({ type: 'success', text: `Admin user ${newAdmin.email} created successfully!` })

      // Reset form and reload
      setNewAdmin({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        password: '',
        role: 'admin',
        adminLevel: 'ministry_leader'
      })
      setIsAddDialogOpen(false)
      loadAdmins()
    } catch (error: any) {
      console.error('Error adding admin:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to add admin user' })
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteAdmin(adminId: string, adminEmail: string) {
    if (!confirm(`Are you sure you want to remove ${adminEmail}? This action cannot be undone.`)) {
      return
    }

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('admin_level')
        .eq('id', authUser.id)
        .single()

      // Only church owners can delete admins
      if (profile?.admin_level !== 'church_owner') {
        setMessage({ type: 'error', text: 'Only church owners can remove admin users' })
        return
      }

      // Prevent deleting yourself
      if (adminId === authUser.id) {
        setMessage({ type: 'error', text: 'You cannot remove yourself' })
        return
      }

      // Delete the user (cascade will handle profile)
      const { error } = await supabase.auth.admin.deleteUser(adminId)

      if (error) throw error

      setMessage({ type: 'success', text: `Admin user removed successfully` })
      loadAdmins()
    } catch (error: any) {
      console.error('Error deleting admin:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to remove admin user' })
    }
  }

  async function handlePromoteMember(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setMessage(null)

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) {
        setMessage({ type: 'error', text: 'Not authenticated' })
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id, admin_level')
        .eq('id', authUser.id)
        .single()

      if (!profile?.church_id) {
        setMessage({ type: 'error', text: 'No church ID found' })
        return
      }

      // Only church owners can promote members
      if (profile.admin_level !== 'church_owner') {
        setMessage({ type: 'error', text: 'Only church owners can promote members' })
        return
      }

      // Get member details
      const selectedMember = members.find(m => m.id === selectedMemberId)
      if (!selectedMember) {
        setMessage({ type: 'error', text: 'Member not found' })
        return
      }

      if (!selectedMember.email) {
        setMessage({ type: 'error', text: 'Member must have an email address to be promoted' })
        return
      }

      // Create auth user for the member
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: selectedMember.email,
        password: promoteData.password,
        email_confirm: true,
        user_metadata: {
          preferred_name: selectedMember.preferred_name
        }
      })

      if (authError) throw authError

      // Create profile entry linked to member
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: selectedMember.email,
          first_name: selectedMember.preferred_name.split(' ')[0] || selectedMember.preferred_name,
          last_name: selectedMember.preferred_name.split(' ').slice(1).join(' ') || '',
          phone: selectedMember.phone_number,
          church_id: profile.church_id,
          role: promoteData.role,
          admin_level: promoteData.role === 'admin' ? promoteData.adminLevel : 'none',
          member_id: selectedMember.id,  // Link to member record
          email_verified: true,
          two_factor_enabled: false,
        })

      if (profileError) throw profileError

      setMessage({
        type: 'success',
        text: `${selectedMember.preferred_name} has been promoted to ${promoteData.role}!`
      })

      // Reset form and reload
      setPromoteData({
        password: '',
        role: 'counselor',
        adminLevel: 'ministry_leader'
      })
      setSelectedMemberId('')
      setIsPromoteDialogOpen(false)
      loadAdmins()
      loadMembers()
    } catch (error: any) {
      console.error('Error promoting member:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to promote member' })
    } finally {
      setSaving(false)
    }
  }

  function getRoleBadge(role: string) {
    switch (role) {
      case 'admin':
        return <Badge variant="default">Admin</Badge>
      case 'counselor':
        return <Badge className="bg-teal-600">Counselor</Badge>
      case 'leader':
        return <Badge variant="secondary">Leader</Badge>
      case 'pastor':
        return <Badge className="bg-indigo-600">Pastor</Badge>
      default:
        return <Badge variant="outline">Member</Badge>
    }
  }

  function getAdminLevelBadge(level: string) {
    switch (level) {
      case 'church_owner':
        return <Badge className="bg-sky-500">Church Owner</Badge>
      case 'church_admin':
        return <Badge className="bg-blue-600">Church Admin</Badge>
      case 'church_elder':
        return <Badge className="bg-green-600">Church Elder</Badge>
      case 'ministry_leader':
        return <Badge variant="secondary">Ministry Leader</Badge>
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Check if user is church owner
  const isChurchOwner = user?.adminLevel === 'church_owner'

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Management</h1>
          <p className="text-muted-foreground">Manage admin users and their permissions</p>
        </div>
        {isChurchOwner && (
          <div className="flex gap-2">
            <Dialog open={isPromoteDialogOpen} onOpenChange={setIsPromoteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!canAddAdmin) {
                      setShowUpgradePrompt(true)
                    } else {
                      setIsPromoteDialogOpen(true)
                    }
                  }}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Promote Member
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] !bg-white dark:!bg-slate-950 border border-border">
                <form onSubmit={handlePromoteMember}>
                  <DialogHeader>
                    <DialogTitle>Promote Member to Admin/Counselor</DialogTitle>
                    <DialogDescription>
                      Select an existing church member and give them admin or counseling access
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="member">Select Member *</Label>
                      <select
                        id="member"
                        value={selectedMemberId}
                        onChange={(e) => setSelectedMemberId(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        required
                      >
                        <option value="">Choose a member...</option>
                        {members.map(member => (
                          <option key={member.id} value={member.id}>
                            {member.preferred_name} {member.email ? `(${member.email})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {members.length === 0
                          ? 'All active members are already promoted'
                          : `${members.length} members available for promotion`}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="promotePassword">Initial Password *</Label>
                      <Input
                        id="promotePassword"
                        type="password"
                        value={promoteData.password}
                        onChange={(e) => setPromoteData({ ...promoteData, password: e.target.value })}
                        placeholder="Minimum 8 characters"
                        required
                        minLength={8}
                      />
                      <p className="text-xs text-muted-foreground">
                        They can change this after first login
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="promoteRole">Role *</Label>
                      <select
                        id="promoteRole"
                        value={promoteData.role}
                        onChange={(e) => setPromoteData({ ...promoteData, role: e.target.value as any })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="counselor">Counselor (Counseling Only)</option>
                        <option value="admin">Admin (Full System Access)</option>
                        <option value="leader">Leader</option>
                        <option value="pastor">Pastor</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {promoteData.role === 'counselor' && 'Can receive and manage counseling requests only'}
                        {promoteData.role === 'admin' && 'Full access to admin dashboard with permission level below'}
                        {promoteData.role === 'leader' && 'Can manage events and view members'}
                        {promoteData.role === 'pastor' && 'Pastoral access including counseling'}
                      </p>
                    </div>

                    {promoteData.role === 'admin' && (
                      <div className="space-y-2">
                        <Label htmlFor="promoteAdminLevel">Admin Permission Level *</Label>
                        <select
                          id="promoteAdminLevel"
                          value={promoteData.adminLevel}
                          onChange={(e) => setPromoteData({ ...promoteData, adminLevel: e.target.value as any })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <option value="ministry_leader">Ministry Leader</option>
                          <option value="church_elder">Church Elder</option>
                          <option value="church_admin">Church Admin</option>
                          <option value="church_owner">Church Owner</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                          {getAdminLevelDescription(promoteData.adminLevel as any)}
                        </p>
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsPromoteDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving || !selectedMemberId}>
                      {saving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Promoting...
                        </>
                      ) : (
                        'Promote Member'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => {
                    if (!canAddAdmin) {
                      setShowUpgradePrompt(true)
                    } else {
                      setIsAddDialogOpen(true)
                    }
                  }}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add New User
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] !bg-white dark:!bg-slate-950 border border-border">
                <form onSubmit={handleAddAdmin}>
                  <DialogHeader>
                    <DialogTitle>Add New Admin User</DialogTitle>
                  <DialogDescription>
                    Create a new admin account with specific permissions
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={newAdmin.firstName}
                        onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={newAdmin.lastName}
                        onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                      placeholder="admin@yourchurch.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={newAdmin.phone}
                      onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                      placeholder="+233 24 014 4723"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Initial Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                      They can change this after first login
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <select
                      id="role"
                      value={newAdmin.role}
                      onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value as any })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="admin">Admin (Full System Access)</option>
                      <option value="counselor">Counselor (Counseling Only)</option>
                      <option value="leader">Leader</option>
                      <option value="pastor">Pastor</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      {newAdmin.role === 'counselor' && 'Can receive and manage counseling requests only'}
                      {newAdmin.role === 'admin' && 'Full access to admin dashboard with permission level below'}
                      {newAdmin.role === 'leader' && 'Can manage events and view members'}
                      {newAdmin.role === 'pastor' && 'Pastoral access including counseling'}
                    </p>
                  </div>

                  {newAdmin.role === 'admin' && (
                    <div className="space-y-2">
                      <Label htmlFor="adminLevel">Admin Permission Level *</Label>
                      <select
                        id="adminLevel"
                        value={newAdmin.adminLevel}
                        onChange={(e) => setNewAdmin({ ...newAdmin, adminLevel: e.target.value as any })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="ministry_leader">Ministry Leader</option>
                        <option value="church_elder">Church Elder</option>
                        <option value="church_admin">Church Admin</option>
                        <option value="church_owner">Church Owner</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {getAdminLevelDescription(newAdmin.adminLevel as any)}
                      </p>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Admin User'
                    )}
                  </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'bg-red-50 border-red-200 dark:bg-red-950/50 dark:border-red-900' : 'bg-green-50 border-green-200 dark:bg-green-950/50 dark:border-green-900'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          )}
          <AlertDescription className={message.type === 'error' ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Permission Levels Info */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-sky-500" />
              <CardTitle className="text-sm">Church Owner</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Full system access. Can add/remove admins, manage all settings, and view all data.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-sm">Church Admin</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Can manage members, events, finances, and communications. Cannot manage admin users.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              <CardTitle className="text-sm">Church Elder</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Can manage members and events, view reports. No finance access or admin management.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-gray-600" />
              <CardTitle className="text-sm">Ministry Leader</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Limited access to their specific ministry area. Can view members and manage events.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-sm">Counselor</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Specialized role for handling counseling requests. Can view and manage assigned cases only.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Admin Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Users ({admins.length})</CardTitle>
          <CardDescription>
            All administrative users with access to the church management system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Access Level</TableHead>
                <TableHead>2FA Status</TableHead>
                <TableHead>Added</TableHead>
                {isChurchOwner && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">
                      <div>
                        <p>{admin.first_name} {admin.last_name}</p>
                        {admin.id === user?.id && (
                          <Badge variant="outline" className="text-xs mt-1">You</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center text-sm">
                          <Mail className="mr-2 h-3 w-3" />
                          {admin.email}
                        </div>
                        {admin.phone && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Phone className="mr-2 h-3 w-3" />
                            {admin.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {getRoleBadge(admin.role)}
                        {admin.role === 'admin' && admin.admin_level && admin.admin_level !== 'none' && getAdminLevelBadge(admin.admin_level)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {admin.two_factor_enabled ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <Key className="mr-1 h-3 w-3" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-600">
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </TableCell>
                    {isChurchOwner && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" disabled={admin.id === user?.id}>
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={admin.id === user?.id}
                            onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isChurchOwner ? 6 : 5} className="text-center py-8 text-muted-foreground">
                    No admin users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!isChurchOwner && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You need Church Owner permissions to add or remove admin users. Contact your church owner if you need to make changes.
          </AlertDescription>
        </Alert>
      )}

      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        limitType="admins"
        currentPlan={limits?.plan || 'starter'}
        currentUsage={current}
        maxLimit={max}
      />
    </div>
  )
}
